import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Section } from '../Common/PageLayout';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
    panel: { border: `1px solid ${tokens.border}`, borderRadius: tokens.radius, overflow: 'hidden', backgroundColor: tokens.white },
    imageWrap: { minHeight: 360, backgroundColor: tokens.blue50, [theme.breakpoints.down('sm')]: { minHeight: 280 } },
    image: { display: 'block', width: '100%', height: '100%', minHeight: 360, maxHeight: 520, objectFit: 'cover', objectPosition: 'center top', [theme.breakpoints.down('sm')]: { minHeight: 280, maxHeight: 420 } },
    body: { padding: theme.spacing(6), [theme.breakpoints.down('sm')]: { padding: theme.spacing(3.5) } },
    overline: { display: 'block', color: tokens.blue600, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: theme.spacing(2) },
    title: { marginBottom: theme.spacing(2.5), color: tokens.ink },
    content: { color: tokens.inkSoft, fontSize: '1rem', lineHeight: 1.75, '& p': { margin: `0 0 ${theme.spacing(2)}px` }, '& p:last-child': { marginBottom: 0 } },
    signature: { marginTop: theme.spacing(3), paddingTop: theme.spacing(3), borderTop: `1px solid ${tokens.border}` },
    name: { fontFamily: tokens.headingFont, fontWeight: 600, fontSize: '1.0625rem', color: tokens.ink, margin: 0 },
    role: { color: tokens.muted, fontSize: '0.9375rem', margin: '2px 0 0' },
    actions: { marginTop: theme.spacing(3), display: 'flex', gap: theme.spacing(2), flexWrap: 'wrap' },
}));

function getMediaUrl(media) {
    if (!media) return '';
    return media.fields?.file?.url || media.url || media.formats?.medium?.url || media.formats?.small?.url || '';
}

function getValue(page, keys, fallback = '') {
    const key = keys.find((candidate) => page[candidate] !== undefined && page[candidate] !== null && page[candidate] !== '');
    return key ? page[key] : fallback;
}

function CmsLink({ label, href, primary }) {
    if (!label || !href) return null;
    const isExternal = /^https?:\/\//i.test(href);
    const linkProps = isExternal ? { href, target: '_blank', rel: 'noreferrer' } : { component: RouterLink, to: href };
    return <Button {...linkProps} variant={primary ? 'contained' : 'outlined'} color={primary ? 'primary' : undefined} endIcon={primary ? <ArrowForwardIcon /> : undefined}>{label}</Button>;
}

export default function MinistryMessagePage({ page }) {
    const classes = useStyles();
    const title = getValue(page, ['calloutTitle', 'messageTitle', 'title'], 'Ministry message');
    const eyebrow = getValue(page, ['eyebrow', 'overline'], 'Office of the Minister');
    const imageUrl = getMediaUrl(getValue(page, ['calloutImage', 'messageImage', 'cover_page', 'mediafile', 'image', 'media', 'menupic']));
    const content = getValue(page, ['calloutContent', 'messageContent', 'description', 'content']);
    const ministerName = getValue(page, ['ministerName', 'signatureName', 'authorName']);
    const ministerRole = getValue(page, ['ministerRole', 'signatureRole', 'authorRole']);
    const primaryLabel = getValue(page, ['primaryButtonLabel', 'primaryCtaLabel']);
    const primaryUrl = getValue(page, ['primaryButtonUrl', 'primaryCtaUrl']);
    const secondaryLabel = getValue(page, ['secondaryButtonLabel', 'secondaryCtaLabel']);
    const secondaryUrl = getValue(page, ['secondaryButtonUrl', 'secondaryCtaUrl']);

    return (
        <Section tone="default">
            <div className={classes.panel}>
                <Grid container>
                    <Grid item xs={12} md={4} className={classes.imageWrap}>
                        {imageUrl && <img src={imageUrl} alt={getValue(page, ['imageAlt'], title)} className={classes.image} />}
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <div className={classes.body}>
                            {eyebrow && <span className={classes.overline}>{eyebrow}</span>}
                            <Typography variant="h3" component="h1" className={classes.title}>{title}</Typography>
                            {content && <div className={classes.content} dangerouslySetInnerHTML={{ __html: content }} />}
                            {(ministerName || ministerRole) && <div className={classes.signature}>
                                {ministerName && <p className={classes.name}>{ministerName}</p>}
                                {ministerRole && <p className={classes.role}>{ministerRole}</p>}
                            </div>}
                            {(primaryLabel || secondaryLabel) && <div className={classes.actions}>
                                <CmsLink label={primaryLabel} href={primaryUrl} primary />
                                <CmsLink label={secondaryLabel} href={secondaryUrl} />
                            </div>}
                        </div>
                    </Grid>
                </Grid>
            </div>
        </Section>
    );
}